import React, { useCallback, useEffect, useMemo, useRef } from "react";
import lockIconAnimation from "../../../animations/svg/LockForToken_data.json";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { AnimationSegment } from "lottie-web";
import { Token } from "../../../store/global/TokenEnum";
import { GlobalStore } from "../../../store/global/GlobalStore";

export interface LockButtonProps {
  symbol: Token;
  canLock: boolean;
  isLocked: boolean;
  isVisible: boolean;
  onClick: () => void;
}

const ANIMATION_MAP: Record<string, AnimationSegment> = {
  UNLOCK_MOUSE_ENTER: [30, 50],
  LOCKED_MOUSE_ENTER: [90, 95],
  LOCKED_MOUSE_LEAVE: [85, 90],
  LOCK_TOKEN: [58, 68],
  UNLOCK_TOKEN: [0, 5],
  DISABLE_TOKEN: [180, 185],
};

export const LockButton: React.FC<LockButtonProps> = props => {
  const animation = useRef<LottieRefCurrentProps>(null);
  const isLocked = GlobalStore.useState(
    s => s.makerTokens.find(makerToken => makerToken.token === props.symbol)?.isLocked
  );

  const playAnimation = useCallback((segments: AnimationSegment) => {
    animation.current?.playSegments(segments, false);
  }, []);

  useEffect(() => {
    if (!props.isLocked && !props.canLock) {
      playAnimation(ANIMATION_MAP.DISABLE_TOKEN);
    }
  }, [props.isLocked, props.canLock, playAnimation]);

  const handleOnClick = useCallback(() => {
    const setIsLocked = (newIsLockedState: boolean) =>
      GlobalStore.update(s => {
        const currentTokenState = s.makerTokens.find(
          makerToken => makerToken.token === props.symbol
        );
        if (currentTokenState != null) {
          currentTokenState.isLocked = newIsLockedState;
        }
      });

    if (!props.canLock && !props.isLocked) return;
    props.onClick();
    if (!isLocked) {
      playAnimation(ANIMATION_MAP.LOCK_TOKEN);
      setIsLocked(true);
    } else {
      playAnimation(ANIMATION_MAP.UNLOCK_TOKEN);
      setIsLocked(false);
    }
  }, [isLocked, playAnimation, props]);

  const memoizedAnimation = useMemo(() => {
    return (
      <Lottie
        disabled={!props.canLock}
        animationData={lockIconAnimation}
        loop={false}
        initialSegment={props.isLocked ? [67, 68] : [0, 1]}
        autoPlay={false}
        lottieRef={animation}
        className="lock-button"
        data-testid={`${props.symbol + "-"}lock-btn`}
      />
    );
  }, [props.canLock, props.isLocked, props.symbol]);

  if (!props.isVisible) {
    return null;
  }

  return (
    <div
      className={"percentage d-flex align-items-start mb-1"}
      onClick={handleOnClick}
      onMouseEnter={() => {
        if (!props.isLocked && props.canLock) {
          playAnimation(ANIMATION_MAP.UNLOCK_MOUSE_ENTER);
        } else if (props.isLocked) {
          playAnimation(ANIMATION_MAP.LOCKED_MOUSE_ENTER);
        }
      }}
      onMouseLeave={() => {
        if (isLocked) {
          playAnimation(ANIMATION_MAP.LOCKED_MOUSE_LEAVE);
        }
      }}
    >
      {memoizedAnimation}
    </div>
  );
};
